import { availablePalette, describeStatement, } from "./registry.js";
import { el } from "../dom.js";
const DND_PALETTE = "application/x-sieve-palette";
const DND_STMT = "application/x-sieve-stmt";
function cloneStatements(statements) {
    return structuredClone(statements);
}
function getBlock(statements, path) {
    let current = statements;
    for (let i = 0; i < path.length; i++) {
        const seg = path[i];
        if (typeof seg === "number") {
            const stmt = current[seg];
            if (!stmt || stmt.type !== "if") {
                throw new Error("Invalid block path");
            }
            const next = path[i + 1];
            if (next === "then") {
                current = stmt.then;
                i += 1;
            }
            else if (next === "else") {
                current = stmt.else ?? [];
                i += 1;
            }
            else if (typeof next === "object" && next && "elsif" in next) {
                const branch = stmt.elsif[next.elsif];
                if (!branch)
                    throw new Error("Invalid elsif path");
                current = branch.then;
                i += 1;
            }
            else {
                throw new Error("Expected then/else/elsif after index");
            }
        }
        else {
            throw new Error("Invalid block path segment");
        }
    }
    return current;
}
function updateBlock(statements, path, nextBlock) {
    const root = cloneStatements(statements);
    if (path.length === 0) {
        return nextBlock;
    }
    let current = root;
    for (let i = 0; i < path.length; i++) {
        const seg = path[i];
        if (typeof seg !== "number")
            throw new Error("Invalid path");
        const stmt = current[seg];
        if (!stmt || stmt.type !== "if")
            throw new Error("Expected if");
        const next = path[i + 1];
        if (next === "then") {
            if (i + 1 === path.length - 1) {
                stmt.then = nextBlock;
                return root;
            }
            current = stmt.then;
            i += 1;
        }
        else if (next === "else") {
            if (!stmt.else)
                stmt.else = [];
            if (i + 1 === path.length - 1) {
                stmt.else = nextBlock;
                return root;
            }
            current = stmt.else;
            i += 1;
        }
        else if (typeof next === "object" && next && "elsif" in next) {
            const branch = stmt.elsif[next.elsif];
            if (!branch)
                throw new Error("Invalid elsif");
            if (i + 1 === path.length - 1) {
                branch.then = nextBlock;
                return root;
            }
            current = branch.then;
            i += 1;
        }
        else {
            throw new Error("Expected block segment");
        }
    }
    return root;
}
function removeFromBlock(statements, path, index) {
    const block = [...getBlock(statements, path)];
    if (index < 0 || index >= block.length) {
        return { statements, removed: null };
    }
    const [removed] = block.splice(index, 1);
    return { statements: updateBlock(statements, path, block), removed };
}
function insertIntoBlock(statements, path, index, node) {
    const block = [...getBlock(statements, path)];
    const at = Math.max(0, Math.min(index, block.length));
    block.splice(at, 0, node);
    return updateBlock(statements, path, block);
}
function pathsEqual(a, b) {
    return JSON.stringify(a) === JSON.stringify(b);
}
/** True if `inner` is the same as `outer` or nested under it (including under a statement in outer). */
function isNestedUnder(outer, stmtIndex, inner) {
    if (inner.length < outer.length + 1)
        return false;
    for (let i = 0; i < outer.length; i++) {
        if (JSON.stringify(outer[i]) !== JSON.stringify(inner[i]))
            return false;
    }
    return inner[outer.length] === stmtIndex;
}
function fieldRow(label, input) {
    const row = el("label", "sieve-field");
    row.append(el("span", "sieve-field-label", label), input);
    return row;
}
function textInput(value, onInput) {
    const input = el("input", "input");
    input.type = "text";
    input.value = value;
    input.addEventListener("input", () => onInput(input.value));
    return input;
}
function numberInput(value, onInput) {
    const input = el("input", "input");
    input.type = "number";
    input.value = String(value);
    input.addEventListener("input", () => onInput(Number(input.value) || 0));
    return input;
}
function selectInput(value, options, onChange) {
    const select = el("select", "input");
    for (const opt of options) {
        const o = el("option", undefined, opt.label);
        o.value = opt.value;
        if (opt.value === value)
            o.selected = true;
        select.append(o);
    }
    select.addEventListener("change", () => onChange(select.value));
    return select;
}
function editTest(test, onChange) {
    const wrap = el("div", "sieve-prop-block");
    if (test.type === "header" || test.type === "address") {
        wrap.append(fieldRow("Headers", textInput(test.headers.join(", "), (v) => onChange({ ...test, headers: v.split(",").map((s) => s.trim()).filter(Boolean) }))), fieldRow("Match", selectInput(test.match.matchType ?? "contains", [
            { value: "is", label: "is" },
            { value: "contains", label: "contains" },
            { value: "matches", label: "matches" },
            { value: "regex", label: "regex" },
        ], (v) => onChange({
            ...test,
            match: { ...test.match, matchType: v },
        }))), fieldRow("Values", textInput(test.keys.join(", "), (v) => onChange({ ...test, keys: v.split(",").map((s) => s.trim()).filter(Boolean) }))));
    }
    else if (test.type === "envelope") {
        wrap.append(fieldRow("Fields", textInput(test.fields.join(", "), (v) => onChange({ ...test, fields: v.split(",").map((s) => s.trim()).filter(Boolean) }))), fieldRow("Values", textInput(test.keys.join(", "), (v) => onChange({ ...test, keys: v.split(",").map((s) => s.trim()).filter(Boolean) }))));
    }
    else if (test.type === "size") {
        wrap.append(fieldRow("Over bytes", numberInput(test.over ?? 0, (v) => onChange({ type: "size", over: v }))));
    }
    else if (test.type === "exists") {
        wrap.append(fieldRow("Headers", textInput(test.headers.join(", "), (v) => onChange({
            type: "exists",
            headers: v.split(",").map((s) => s.trim()).filter(Boolean),
        }))));
    }
    else if (test.type === "body") {
        wrap.append(fieldRow("Match text", textInput(test.keys.join(", "), (v) => onChange({ ...test, keys: v.split(",").map((s) => s.trim()).filter(Boolean) }))));
    }
    else if (test.type === "opaque") {
        wrap.append(el("p", "hint", `Unsupported test: ${test.text.slice(0, 80)}`));
    }
    else {
        wrap.append(el("p", "hint", `Test: ${test.type}`));
    }
    return wrap;
}
function editAction(action, onChange) {
    const wrap = el("div", "sieve-prop-block");
    if (action.type === "fileinto") {
        wrap.append(fieldRow("Mailbox", textInput(action.mailbox, (v) => onChange({ ...action, mailbox: v }))));
        const copy = el("label", "sieve-check");
        const cb = el("input");
        cb.type = "checkbox";
        cb.checked = !!action.copy;
        cb.addEventListener("change", () => onChange({ ...action, copy: cb.checked }));
        copy.append(cb, document.createTextNode(" Keep a copy"));
        wrap.append(copy);
    }
    else if (action.type === "redirect") {
        wrap.append(fieldRow("Address", textInput(action.address, (v) => onChange({ ...action, address: v }))));
        const copy = el("label", "sieve-check");
        const cb = el("input");
        cb.type = "checkbox";
        cb.checked = !!action.copy;
        cb.addEventListener("change", () => onChange({ ...action, copy: cb.checked }));
        copy.append(cb, document.createTextNode(" Keep a copy"));
        wrap.append(copy);
    }
    else if (action.type === "reject") {
        wrap.append(fieldRow("Reason", textInput(action.reason, (v) => onChange({ ...action, reason: v }))));
    }
    else if (action.type === "vacation") {
        const subject = textInput(action.subject ?? "", (v) => onChange({ ...action, subject: v }));
        const days = numberInput(action.days ?? 7, (v) => onChange({ ...action, days: v }));
        const message = el("textarea", "input");
        message.rows = 4;
        message.value = action.message;
        message.addEventListener("input", () => onChange({ ...action, message: message.value }));
        wrap.append(fieldRow("Subject", subject), fieldRow("Days", days), fieldRow("Message", message));
    }
    else if (action.type === "include") {
        wrap.append(fieldRow("Script", textInput(action.script, (v) => onChange({ ...action, script: v }))));
    }
    else if (action.type === "addflag" ||
        action.type === "setflag" ||
        action.type === "removeflag") {
        wrap.append(fieldRow("Flags", textInput(action.flags.join(", "), (v) => onChange({
            ...action,
            flags: v.split(",").map((s) => s.trim()).filter(Boolean),
        }))));
    }
    else if (action.type === "set") {
        wrap.append(fieldRow("Name", textInput(action.name, (v) => onChange({ ...action, name: v }))), fieldRow("Value", textInput(action.value, (v) => onChange({ ...action, value: v }))));
    }
    else if (action.type === "notify") {
        wrap.append(fieldRow("Method", textInput(action.method, (v) => onChange({ ...action, method: v }))), fieldRow("Message", textInput(action.message ?? "", (v) => onChange({ ...action, message: v }))));
    }
    else if (action.type === "opaque") {
        wrap.append(el("p", "hint", "Unsupported action — edit in Source."));
    }
    else {
        wrap.append(el("p", "hint", action.type));
    }
    return wrap;
}
function replaceInBlock(statements, path, index, nextStmt) {
    const block = [...getBlock(statements, path)];
    block[index] = nextStmt;
    return updateBlock(statements, path, block);
}
function readDropPayload(event) {
    const paletteId = event.dataTransfer?.getData(DND_PALETTE);
    if (paletteId) {
        return { kind: "palette", paletteId };
    }
    const raw = event.dataTransfer?.getData(DND_STMT);
    if (!raw)
        return null;
    try {
        const parsed = JSON.parse(raw);
        return { kind: "stmt", path: parsed.path, index: parsed.index };
    }
    catch {
        return null;
    }
}
function applyDrop(rootStatements, targetPath, targetIndex, payload, palette) {
    if (payload.kind === "palette") {
        const item = palette.find((p) => p.id === payload.paletteId);
        if (!item)
            return null;
        return insertIntoBlock(rootStatements, targetPath, targetIndex, item.createStatement());
    }
    if (payload.kind !== "stmt" ||
        payload.path === undefined ||
        payload.index === undefined) {
        return null;
    }
    // Don't drop a block into itself / its descendants
    if (isNestedUnder(payload.path, payload.index, targetPath)) {
        return null;
    }
    // Same block reorder
    if (pathsEqual(payload.path, targetPath)) {
        const block = [...getBlock(rootStatements, targetPath)];
        if (payload.index < 0 || payload.index >= block.length)
            return null;
        const [moved] = block.splice(payload.index, 1);
        let at = targetIndex;
        if (payload.index < targetIndex)
            at -= 1;
        at = Math.max(0, Math.min(at, block.length));
        block.splice(at, 0, moved);
        return updateBlock(rootStatements, targetPath, block);
    }
    const removed = removeFromBlock(rootStatements, payload.path, payload.index);
    if (!removed.removed)
        return null;
    return insertIntoBlock(removed.statements, targetPath, targetIndex, removed.removed);
}
function wireDropTarget(target, getTarget, rootStatements, palette, onRootChange) {
    target.addEventListener("dragover", (event) => {
        event.preventDefault();
        event.stopPropagation();
        target.classList.add("sieve-drop-hover");
        if (event.dataTransfer)
            event.dataTransfer.dropEffect = "move";
    });
    target.addEventListener("dragleave", () => {
        target.classList.remove("sieve-drop-hover");
    });
    target.addEventListener("drop", (event) => {
        event.preventDefault();
        event.stopPropagation();
        target.classList.remove("sieve-drop-hover");
        const payload = readDropPayload(event);
        if (!payload)
            return;
        const { path, index } = getTarget();
        const next = applyDrop(rootStatements(), path, index, payload, palette);
        if (next)
            onRootChange(next);
    });
}
function renderDropSlot(label, path, index, rootStatements, palette, onRootChange) {
    const slot = el("div", "sieve-drop-slot", label);
    wireDropTarget(slot, () => ({ path, index }), rootStatements, palette, onRootChange);
    return slot;
}
function renderBlockEditor(path, block, rootStatements, palette, onRootChange) {
    const box = el("div", "sieve-nested");
    box.append(renderDropSlot("Drop here", path, 0, rootStatements, palette, onRootChange));
    block.forEach((stmt, index) => {
        box.append(renderStatementEditor(stmt, path, index, rootStatements, palette, onRootChange));
        box.append(renderDropSlot("Drop here", path, index + 1, rootStatements, palette, onRootChange));
    });
    return box;
}
function renderStatementEditor(stmt, path, index, rootStatements, palette, onRootChange) {
    const card = el("div", "sieve-rule-card");
    card.draggable = true;
    const head = el("div", "sieve-rule-head");
    head.append(el("strong", undefined, describeStatement(stmt)));
    const remove = el("button", "btn btn-secondary btn-sm", "Remove");
    remove.type = "button";
    remove.addEventListener("click", () => {
        const next = removeFromBlock(rootStatements(), path, index);
        if (next.removed)
            onRootChange(next.statements);
    });
    head.append(remove);
    card.append(head);
    if (stmt.type === "if") {
        card.append(editTest(stmt.test, (test) => {
            onRootChange(replaceInBlock(rootStatements(), path, index, { ...stmt, test }));
        }));
        card.append(el("p", "hint", "Then (drop Stop / actions here):"));
        card.append(renderBlockEditor([...path, index, "then"], stmt.then, rootStatements, palette, onRootChange));
        stmt.elsif.forEach((branch, ei) => {
            card.append(el("p", "hint", `Elsif ${ei + 1}:`));
            card.append(editTest(branch.test, (test) => {
                const elsif = stmt.elsif.map((b, i) => (i === ei ? { ...b, test } : b));
                onRootChange(replaceInBlock(rootStatements(), path, index, { ...stmt, elsif }));
            }));
            card.append(renderBlockEditor([...path, index, { elsif: ei }], branch.then, rootStatements, palette, onRootChange));
        });
        if (stmt.else) {
            card.append(el("p", "hint", "Else:"));
            card.append(renderBlockEditor([...path, index, "else"], stmt.else, rootStatements, palette, onRootChange));
        }
        else {
            const addElse = el("button", "btn btn-secondary btn-sm", "Add else");
            addElse.type = "button";
            addElse.addEventListener("click", () => {
                onRootChange(replaceInBlock(rootStatements(), path, index, {
                    ...stmt,
                    else: [{ type: "action", action: { type: "keep" } }],
                }));
            });
            card.append(addElse);
        }
    }
    else if (stmt.type === "action") {
        card.append(editAction(stmt.action, (action) => {
            onRootChange(replaceInBlock(rootStatements(), path, index, { type: "action", action }));
        }));
    }
    else if (stmt.type === "opaque") {
        card.append(el("p", "hint", "Unrecognized construct — edit in Source."));
    }
    card.addEventListener("dragstart", (event) => {
        event.stopPropagation();
        event.dataTransfer?.setData(DND_STMT, JSON.stringify({ path, index }));
        event.dataTransfer.effectAllowed = "move";
        card.classList.add("dragging");
    });
    card.addEventListener("dragend", () => card.classList.remove("dragging"));
    // Dropping onto a card inserts before it in the same block
    wireDropTarget(card, () => ({ path, index }), rootStatements, palette, onRootChange);
    return card;
}
export function renderBuilderUi(options) {
    const root = el("div", "sieve-builder");
    if (options.enabled === false) {
        root.classList.add("sieve-builder-disabled");
        root.append(el("p", "muted", "Select a script on the left to start editing."));
        return root;
    }
    const palette = availablePalette(options.capabilities);
    let rootStatements = options.ast.statements.filter((s) => s.type !== "require");
    const getRoot = () => rootStatements;
    const setRoot = (next) => {
        rootStatements = next;
        options.onChange({ ...options.ast, statements: next });
    };
    const paletteCol = el("div", "sieve-palette");
    paletteCol.append(el("h4", undefined, "Add"));
    paletteCol.append(el("p", "hint", "Drag onto a drop zone, or click to add at the end."));
    if (palette.length === 0) {
        paletteCol.append(el("p", "hint", "No blocks available (capabilities missing)."));
    }
    for (const item of palette) {
        const btn = el("button", "btn btn-secondary btn-sm sieve-palette-btn", item.label);
        btn.type = "button";
        btn.draggable = true;
        btn.title = item.requires?.length
            ? `Requires: ${item.requires.join(", ")}. Drag into an If block or drop zone.`
            : "Drag into an If block or drop zone.";
        btn.addEventListener("dragstart", (event) => {
            event.dataTransfer?.setData(DND_PALETTE, item.id);
            event.dataTransfer.effectAllowed = "copyMove";
            btn.classList.add("dragging");
        });
        btn.addEventListener("dragend", () => btn.classList.remove("dragging"));
        btn.addEventListener("click", () => {
            setRoot([...getRoot(), item.createStatement()]);
        });
        paletteCol.append(btn);
    }
    const canvas = el("div", "sieve-canvas");
    canvas.append(el("h4", undefined, "Script"));
    if (options.ast.partial) {
        canvas.append(el("p", "hint", "This script has constructs the visual editor does not fully understand. Prefer Source for those parts."));
    }
    canvas.append(renderBlockEditor([], getRoot(), getRoot, palette, setRoot));
    root.append(paletteCol, canvas);
    return root;
}
